import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Button,
  Stack,
  Tabs,
  Tab,
  Snackbar,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import ComparisonPIGraph from '../components/ComparisonPIGraph';
import DetailedMatchBreakdown from '../components/DetailedMatchBreakdown';
import CandidateInfo from '../components/CandidateInfo';
import CandidateNotes from '../components/CandidateNotes';
import CandidateReferences from '../components/CandidateReferences';
import { candidatesApi, jobsApi, piSyncApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Sync, PictureAsPdf } from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`candidate-tabpanel-${index}`}
      aria-labelledby={`candidate-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const CandidateDetail: React.FC = () => {
  const { jobId, candidateId, id } = useParams<{ jobId: string; candidateId: string; id: string }>();
  const navigate = useNavigate();
  
  // Use candidateId if available (from job context), otherwise use id (from direct candidate route)
  const actualCandidateId = candidateId || id;
  
  const [candidate, setCandidate] = useState<any>(null);
  const [job, setJob] = useState<any>(null);
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const { user } = useAuth();

  const canEdit = user?.role && ['admin', 'hr', 'manager'].includes(user.role);

  // Fetch candidate and job data
  useEffect(() => {
    const fetchData = async () => {
      if (!actualCandidateId) {
        setError('Candidate ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch candidate data and jobs list in parallel
        const [candidateResponse, jobsResponse] = await Promise.all([
          candidatesApi.getById(actualCandidateId),
          jobsApi.getAll({ status: 'all' }) // Get all jobs, not just 'open' ones
        ]);
        
        setCandidate(candidateResponse.data);
        
        // Handle jobs response structure (same as Candidates page)
        setJobs(jobsResponse.data.jobs);
        
        // Fetch job data if jobId is provided
        if (jobId) {
          try {
            const jobResponse = await jobsApi.getById(jobId);
            setJob(jobResponse.data);
          } catch (jobError) {
            console.warn('Job not found for jobId:', jobId);
            // Continue without job data
          }
        } else if ((candidateResponse.data as any).job_id) {
          // If no jobId in URL but candidate has a job_id, fetch that job
          try {
            const jobResponse = await jobsApi.getById((candidateResponse.data as any).job_id.toString());
            setJob(jobResponse.data);
          } catch (jobError) {
            console.warn('Job not found for candidate job_id:', (candidateResponse.data as any).job_id);
            // Continue without job data - job may have been deleted
          }
        }
        
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.response?.data?.message || 'Failed to load candidate details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [actualCandidateId, jobId]);

  // Handle candidate update
  const handleCandidateUpdate = async (updates: any) => {
    if (!candidate) return;
    
    try {
      // Convert updates to FormData for the API
      const formData = new FormData();
      Object.keys(updates).forEach(key => {
        if (updates[key] !== null && updates[key] !== undefined) {
          // Handle JSON fields that need to be stringified
          if (key === 'match_breakdown' || key === 'pi_assessment_data' || key === 'evaluation_data') {
            formData.append(key, typeof updates[key] === 'object' ? JSON.stringify(updates[key]) : updates[key]);
          } else {
            formData.append(key, updates[key]);
          }
        }
      });
      
      const response = await candidatesApi.update(candidate.id.toString(), formData);
      
      // The API returns { message: string, candidate: Candidate }
      setCandidate(response.data.candidate || response.data);
      
      // Show success message
      setSuccessMessage('Candidate updated successfully');
      
      // Don't manage edit mode here - let CandidateInfo handle it via onEditToggle
    } catch (err: any) {
      console.error('Error updating candidate:', err);
      const message = err.response?.data?.message || 'Failed to update candidate';
      setErrorMessage(message);
      
      // Re-throw to let CandidateInfo know the save failed
      throw new Error(message);
    }
  };

  // Handle document upload
  const handleDocumentUpload = async (files: { resume?: File; coverLetter?: File }) => {
    if (!actualCandidateId) return;

    try {
      const formData = new FormData();
      if (files.resume) {
        formData.append('resume', files.resume);
      }
      if (files.coverLetter) {
        formData.append('coverLetter', files.coverLetter);
      }

      const response = await candidatesApi.uploadDocuments(actualCandidateId, formData);
      setCandidate(response.data.candidate || response.data);
      setSuccessMessage('Documents uploaded successfully');
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to upload documents';
      setErrorMessage(message);
      throw new Error(message);
    }
  };

  // Handle PI sync
  const handlePISync = async () => {
    if (!actualCandidateId) return;

    try {
      setIsSyncing(true);
      const response = await piSyncApi.syncCandidate(actualCandidateId);
      
      if (response.data.success) {
        setSuccessMessage('Successfully synced candidate data from PI');
        // Refresh candidate data
        const candidateResponse = await candidatesApi.getById(actualCandidateId);
        setCandidate(candidateResponse.data);
      } else {
        setErrorMessage(response.data.message || 'Failed to sync from PI');
      }
    } catch (error: any) {
      setErrorMessage(error.response?.data?.error || 'Failed to sync from PI');
    } finally {
      setIsSyncing(false);
    }
  };

  // Handle PDF download
  const handleDownloadInsights = async () => {
    if (!actualCandidateId) return;

    try {
      setIsDownloadingPdf(true);
      const response = await piSyncApi.getCandidateInsights(actualCandidateId);
      
      // Create a blob from the response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link and click it
      const link = document.createElement('a');
      link.href = url;
      link.download = `${candidate.first_name}_${candidate.last_name}_PI_Insights.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      setSuccessMessage('PI Insights downloaded successfully');
    } catch (error: any) {
      setErrorMessage(error.response?.data?.error || 'Failed to download PI insights');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // Get ideal ranges for PI comparison
  const getIdealRanges = () => {
    if (!job) return null;
    
    // Only return ideal ranges if they are actually defined
    // Don't default to 0-100 as that would show the entire scale as "ideal"
    const hasIdealRanges = 
      (job.ideal_factor_a_min !== null && job.ideal_factor_a_max !== null) ||
      (job.ideal_factor_b_min !== null && job.ideal_factor_b_max !== null) ||
      (job.ideal_factor_c_min !== null && job.ideal_factor_c_max !== null) ||
      (job.ideal_factor_d_min !== null && job.ideal_factor_d_max !== null);
    
    if (!hasIdealRanges) return null;
    
    return {
      a: (job.ideal_factor_a_min !== null && job.ideal_factor_a_max !== null) 
        ? [job.ideal_factor_a_min, job.ideal_factor_a_max] as [number, number]
        : undefined,
      b: (job.ideal_factor_b_min !== null && job.ideal_factor_b_max !== null)
        ? [job.ideal_factor_b_min, job.ideal_factor_b_max] as [number, number]
        : undefined,
      c: (job.ideal_factor_c_min !== null && job.ideal_factor_c_max !== null)
        ? [job.ideal_factor_c_min, job.ideal_factor_c_max] as [number, number]
        : undefined,
      d: (job.ideal_factor_d_min !== null && job.ideal_factor_d_max !== null)
        ? [job.ideal_factor_d_min, job.ideal_factor_d_max] as [number, number]
        : undefined,
    };
  };

  // Get relationship requirements
  const getRelationshipRequirements = () => {
    if (!job) return {};
    
    return {
      requires_a_greater_than_b: !!job.requires_a_greater_than_b,
      requires_a_greater_than_c: !!job.requires_a_greater_than_c,
      requires_a_greater_than_d: !!job.requires_a_greater_than_d,
      requires_b_greater_than_a: !!job.requires_b_greater_than_a,
      requires_b_greater_than_c: !!job.requires_b_greater_than_c,
      requires_b_greater_than_d: !!job.requires_b_greater_than_d,
      requires_c_greater_than_a: !!job.requires_c_greater_than_a,
      requires_c_greater_than_b: !!job.requires_c_greater_than_b,
      requires_c_greater_than_d: !!job.requires_c_greater_than_d,
      requires_d_greater_than_a: !!job.requires_d_greater_than_a,
      requires_d_greater_than_b: !!job.requires_d_greater_than_b,
      requires_d_greater_than_c: !!job.requires_d_greater_than_c,
    };
  };

  // Check if candidate has PI data
  const hasPIData = candidate?.pi_factor_a && candidate?.pi_factor_b && 
                   candidate?.pi_factor_c && candidate?.pi_factor_d;

  // Parse match breakdown
  let matchBreakdown = null;
  if (candidate?.match_breakdown) {
    try {
      matchBreakdown = typeof candidate.match_breakdown === 'string' 
        ? JSON.parse(candidate.match_breakdown) 
        : candidate.match_breakdown;
    } catch (e) {
      console.error('Failed to parse match breakdown:', e);
    }
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !candidate) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Candidate not found'}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          variant="outlined"
        >
          Go Back
        </Button>
      </Container>
    );
  }

  const idealRanges = getIdealRanges();
  const relationshipRequirements = getRelationshipRequirements();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} data-testid="candidate-detail-page">
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/jobs"
          onClick={(e) => {
            e.preventDefault();
            navigate('/jobs');
          }}
        >
          Jobs
        </Link>
        {job && (
          <Link
            color="inherit"
            href={`/jobs/${job.id}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/jobs/${job.id}`);
            }}
          >
            {job.title}
          </Link>
        )}
        <Typography color="text.primary">
          {candidate.first_name} {candidate.last_name}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }} data-testid="candidate-header">
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h4" gutterBottom>
              {candidate.first_name} {candidate.last_name}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {job?.title || candidate.job_title || 'Position not specified'}
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={2}>
            {candidate.pi_person_id && (
              <Button
                startIcon={<PictureAsPdf />}
                onClick={handleDownloadInsights}
                variant="outlined"
                disabled={isDownloadingPdf}
                data-testid="download-insights-button"
              >
                {isDownloadingPdf ? 'Downloading...' : 'PI Insights'}
              </Button>
            )}
            <Button
              startIcon={<Sync />}
              onClick={handlePISync}
              variant="outlined"
              disabled={isSyncing}
              data-testid="sync-pi-button"
            >
              {isSyncing ? 'Syncing...' : 'Sync from PI'}
            </Button>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              variant="outlined"
              data-testid="back-button"
            >
              Back
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} data-testid="candidate-tabs">
          <Tab label="Overview" data-testid="tab-overview" />
          {hasPIData && idealRanges && <Tab label="PI Analysis" data-testid="tab-evaluation" />}
          {hasPIData && matchBreakdown && <Tab label="Match Breakdown" data-testid="tab-match-breakdown" />}
          <Tab label="Notes" data-testid="tab-notes" />
          <Tab label="References" data-testid="tab-references" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {/* Overview - Quick summary */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
            gap: 3,
          }}
        >
          <Box>
            <CandidateInfo
              candidate={candidate}
              onUpdate={handleCandidateUpdate}
              onDocumentUpload={canEdit ? handleDocumentUpload : undefined}
              isEditing={isEditing}
              onEditToggle={() => setIsEditing(!isEditing)}
              jobs={jobs}
            />
          </Box>
          
          {hasPIData && idealRanges && (
            <Box>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Quick PI Overview
                </Typography>
                {matchBreakdown && candidate.position_match_score && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h4" color="primary.main" data-testid="position-match-score">
                      {Math.round(candidate.position_match_score * 10)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Position Match
                    </Typography>
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary">
                  View the PI Analysis tab for detailed behavioral comparison.
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>
      </TabPanel>

      {hasPIData && idealRanges && (
        <TabPanel value={tabValue} index={1}>
          {/* PI Analysis */}
          <ComparisonPIGraph
            candidate={candidate}
            idealRanges={idealRanges}
            relationshipRequirements={relationshipRequirements}
            matchBreakdown={matchBreakdown}
          />
        </TabPanel>
      )}

      {hasPIData && matchBreakdown && (
        <TabPanel value={tabValue} index={hasPIData && idealRanges ? 2 : 1}>
          {/* Match Breakdown */}
          <DetailedMatchBreakdown
            candidate={candidate}
            matchBreakdown={matchBreakdown}
            job={job}
          />
        </TabPanel>
      )}

      <TabPanel value={tabValue} index={hasPIData && idealRanges && matchBreakdown ? 3 : hasPIData && idealRanges ? 2 : 1}>
        {/* Notes */}
        <CandidateNotes candidateId={actualCandidateId!} />
      </TabPanel>

      <TabPanel value={tabValue} index={hasPIData && idealRanges && matchBreakdown ? 4 : hasPIData && idealRanges ? 3 : 2}>
        {/* References */}
        <CandidateReferences candidateId={actualCandidateId!} />
      </TabPanel>

      {/* Success Snackbar */}
      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CandidateDetail;